import React, { useState, useEffect } from "react";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import Link from "@mui/material/Link";
import { enqueueSnackbar } from "notistack";
import { getAPICall } from "../../../lib/callApi";

const sizeStyles = {
  small: {
    fontSize: "12px",
    padding: "10px 16px",
  },
  medium: {
    fontSize: "14px",
    padding: "11px 20px",
  },
  large: {
    fontSize: "16px",
    padding: "12px 24px",
  },
};

const typeStyles = {
  primary: {
    color: "#000000",
    backgroundColor: "#ffffff",
    transition: "background-color .20s ease-in",
    borderRadius: ".9rem",
    padding: ".5rem 1rem",
    fontWeight: 600,
    lineHeight: "1.75rem",
    width: "100%",
    boxShadow: "none",
    border: ".5px solid black",
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "#000000",
      boxShadow: "none",
    },
  },
  google: {
    color: "#ffffff",
    backgroundColor: "#4285F4",
    transition: "background-color .20s ease-in",
    borderRadius: ".5rem",
    padding: ".5rem 1rem",
    fontWeight: 500,
    lineHeight: "1.75rem",
    width: "100%",
    boxShadow: "none",
    position: "relative",
    paddingLeft: "48px",
    "&:hover": {
      backgroundColor: "#3367D6",
      boxShadow: "none",
    },
    "&:focus": {
      outline: "none",
      boxShadow: "0 0 0 2px #4285F4, 0 0 0 4px rgba(255, 255, 255, 0.8)",
    },
  },
};

const providerDefinitions = {
  jumpcloud: 1,
  google: 2,
};

const SSOButton = ({ provider, clientId, type = "primary", size = "medium", sx = {} }) => {
  const [providerDefinition, setProviderDefinition] = useState();

  useEffect(() => {
    if (provider) {
      (async () => {
        try {
          const response = await getAPICall(
            `sso/calcStartUrl/${provider}${clientId ? `?clientId=${clientId}` : ""}`
          );
          if (response.status === 200) {
            setProviderDefinition(response.data);
          } else
            throw new Error(
              "Invalid provider or client id. Please contact your Alysio rep for more details."
            );
        } catch (err) {
          console.log("Err retrieving sso start url");
          console.log(err);
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: err?.response?.data?.errMessage || err.message || "Something went wrong!",
            }),
            {
              variant: "custom",
            }
          );
        }
      })();
    }
  }, [provider, clientId]);

  if (!provider || !providerDefinitions[provider] || !providerDefinition) return null;

  // Use Google-specific styling and layout if the provider is Google
  if (provider === "google") {
    const googleLogoSvg =
      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJNMTcuNiA5LjJsLS4xLTEuOEg5djMuNGg0LjhDMTMuNiAxMiAxMyAxMyAxMiAxMy42djIuMmgzYTggOCAwIDAgMCAyLjYtNi42eiIgZmlsbD0iIzQyODVGNCIgZmlsbC1ydWxlPSJub256ZXJvIi8+PHBhdGggZD0iTTkgMThjMi40IDAgNC41LS44IDYtMi4ybC0zLTIuMmE1LjQgNS40IDAgMCAxLTgtMi44SDFWMTNhOSA5IDAgMCAwIDggNXoiIGZpbGw9IiMzNEE4NTMiIGZpbGwtcnVsZT0ibm9uemVybyIvPjxwYXRoIGQ9Ik00IDEwLjdhNS40IDUuNCAwIDAgMSAwLTMuNFY1SDFhOSA5IDAgMCAwIDAgOGwzLTIuM3oiIGZpbGw9IiNGQkJDMDUiIGZpbGwtcnVsZT0ibm9uemVybyIvPjxwYXRoIGQ9Ik05IDMuNmMyLjItLjEgNC4xLjcgNS41IDIuMWwyLjYtMi42QzE1LjUgMS4xIDEyLjUgMCA5IDAgNS40IDAgMi41IDIuMSAxIDV2Mi42bDMgMi4zQTUuNCA1LjQgMCAwIDEgOSAzLjZ6IiBmaWxsPSIjRUE0MzM1IiBmaWxsLXJ1bGU9Im5vbnplcm8iLz48cGF0aCBkPSJNMCAwSDE4VjE4SDB6Ii8+PC9nPjwvc3ZnPg==";

    return (
      <Link href={providerDefinition.href} sx={{ textDecoration: "none" }}>
        <MDButton
          color="google"
          size={size}
          sx={{ ...sizeStyles[size], ...typeStyles.google, ...sx }}
        >
          <MDBox
            sx={{
              position: "absolute",
              left: "4px",
              top: "4px",
              bottom: "4px",
              backgroundColor: "#ffffff",
              borderRadius: ".25rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "36px",
              boxShadow: "1px 0 2px rgba(0,0,0,0.05)",
            }}
          >
            <MDBox
              component="img"
              src={googleLogoSvg}
              alt="Google Brand Logo"
              width="18px"
              height="18px"
              sx={{
                display: "block",
              }}
            />
          </MDBox>
          Sign in with Google
        </MDButton>
      </Link>
    );
  }

  return (
    <Link href={providerDefinition.href}>
      <MDButton color={type} size={size} sx={{ ...sizeStyles[size], ...typeStyles[type], ...sx }}>
        <MDBox
          component="img"
          src={providerDefinition.logoUrl}
          alt={`${providerDefinition.label} Brand Logo`}
          width="40px"
          height="30px"
          sx={{
            marginRight: "8px",
          }}
        />
        {providerDefinition.label}
      </MDButton>
    </Link>
  );
};

export default SSOButton;
